import { ExpandDownLight as ArrowIcon } from '@repo/go-icons';
import { Collapse } from '@repo/go-ui';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import type { Key, ReactNode, Ref } from 'react';

import { CollapseItem } from './components/well-collapse-item/WellCollapseItem';
import type { IWellCollapseItemEntity } from './components/well-collapse-item/WellCollapseItem.entity';
import type { ICollapseEntity } from './WellCollapse.entity';
import styles from './WellCollapse.module.scss';

type Props<T extends IWellCollapseItemEntity> = {
  collapses: ICollapseEntity<T>[];
  activeId?: number;
  onItemSelect(well: T): void;
  refAt?(key: number): Ref<HTMLDivElement>;
  activeKey: string[];
  renderIcon?: (item: T) => ReactNode;
  onChange(key: Key[] | undefined): void;
  renderButtons?(item: T): ReactNode;
};

export const WellCollapse = observer(function WellCollapse<T extends IWellCollapseItemEntity>({
  collapses,
  activeId,
  onItemSelect,
  refAt,
  renderButtons,
  activeKey,
  renderIcon,
  onChange,
}: Props<T>) {
  return (
    <Collapse activeKeys={activeKey} collapseClassname={styles.wrapper} onChange={onChange}>
      {collapses.map((collapse) => (
        <Collapse.Panel
          panelKey={collapse.id}
          key={collapse.id}
          header={
            <div className={styles.panelHeader}>
              <ArrowIcon
                className={clsx(
                  styles.panelHeaderArrow,
                  activeKey.includes(collapse.id) && styles.panelHeaderArrowDown,
                )}
              />
              <p className={styles.panelHeaderText}>{collapse.name}</p>
            </div>
          }
          styleBody={{ gridTemplateColumns: '100%' }}
        >
          <ul className={styles.list}>
            {collapse.items.map((item) => {
              const renderItemButtons = renderButtons ? () => renderButtons(item) : undefined;

              return (
                <CollapseItem<T>
                  key={item.id}
                  renderIcon={renderIcon}
                  renderButtons={renderItemButtons}
                  refAt={refAt}
                  isActive={activeId === item.id}
                  item={item}
                  onClick={() => onItemSelect(item)}
                />
              );
            })}
          </ul>
        </Collapse.Panel>
      ))}
    </Collapse>
  );
});
